"use client";

import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import { useQuery } from "@tanstack/react-query";
import type { CustomAgentDto } from "@akili/types";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";
import { MonoEyebrow } from "@akili/auth-kit";
import { TOUCH_TARGET } from "@akili/ui-responsive";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  useAuthThemeV2,
  V2Accent,
  V2_MUTE,
  V2_OUTLINE_BUTTON,
  V2_PRIMARY_CTA,
} from "@/components/v2-theme";
import { EDITOR_CONTROL_CLASS } from "@/components/studio/editor-shell";
import { api, type LimitKind, type LimitReachedError } from "@/lib/api-client";
import {
  formatMoneyCents,
  isPurchasablePlan,
  type PlanCatalogEntry,
} from "@/components/pricing/plan-catalog";
import {
  INTRO_OFFER_QUERY_KEY,
  type IntroOfferArm,
} from "@/lib/limit-reached-analytics";
import { useActiveOrganization } from "@/lib/auth-client";
import { usePlan } from "@/hooks/use-plan";
import { cn } from "@/lib/utils";

/**
 * ONB-063 (finding F16) — the prompt has to survive a 344dp screen.
 *
 * `DialogContent` is `fixed top-1/2 -translate-y-1/2` with no height bound of
 * its own, so a dialog taller than the viewport is centred on it and loses
 * its own title off the top edge, with nothing to scroll. This one can get
 * tall: the crowded case renders a free-alternative paragraph, a target line,
 * an intro-offer panel and FOUR actions (intro price, full price, the honest
 * alternative, dismiss), and at 344dp those actions wrap onto three rows of
 * 44px each.
 *
 * So: a real ceiling, and the overflow scrolls INSIDE the dialog. `svh`
 * rather than `vh` for the same reason the onboarding shells use it — `vh`
 * is the largest viewport height on mobile, which is the one measurement
 * that does not fit. 5rem of it is given back so the dialog clears the notch
 * at the top and the home indicator at the bottom, both of which are around
 * 34px and neither of which a centred `max-height` would otherwise respect.
 *
 * `overflow-y-auto` and not `overflow-auto`: nothing here may scroll
 * sideways.
 */
const DIALOG_VIEWPORT_CLASS = "max-h-[calc(100svh-5rem)] overflow-y-auto";

// ── V2-THEME-PLANS U4: the V2 skin ─────────────────────────────────────────
//
// Behind `useAuthThemeV2()` only. Same Radix dialog, same copy, same actions
// and outcomes; only the surface changes. `DialogContent` always mounts its
// own `bg-black/50` overlay, and the V2 overlay is a light blur of the app
// instead, so the V2 branch composes the same primitives `components/ui/dialog`
// exports (portal, overlay, close) around Radix's `Content` rather than
// editing the shared component.

/** The overlay: the app stays visible, blurred and lightly tinted. */
const V2_OVERLAY_CLASS = cn(
  "bg-[rgba(11,27,63,0.28)] dark:bg-black/50",
  "supports-[backdrop-filter]:backdrop-blur-[6px]",
);

/**
 * The glass card. Positioning and animation match `DialogContent`; the
 * 16px gutter (`w-[calc(100%-2rem)]`) keeps the card off a phone's edges,
 * and the viewport ceiling is the same `DIALOG_VIEWPORT_CLASS` as legacy.
 */
const V2_CARD_CLASS = cn(
  "fixed left-1/2 top-1/2 z-50 grid w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 gap-4 p-6 sm:p-7",
  "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
  "rounded-auth-card border-auth-glass-line bg-auth-card/90 border supports-[backdrop-filter]:backdrop-blur-[var(--auth-glass-blur)]",
  "shadow-[inset_0_1px_0_var(--auth-glass-highlight),0_30px_70px_-30px_rgba(11,27,63,0.55)]",
  "font-auth-sans text-auth-text",
  DIALOG_VIEWPORT_CLASS,
);

/** The close X, as `DialogContent` draws it, on the V2 text roles. */
const V2_CLOSE_CLASS = cn(
  "text-auth-mute hover:text-auth-text absolute end-2 top-2 inline-flex items-center justify-center rounded-full transition-colors",
  "focus-visible:outline-auth-indigo focus-visible:outline-2 focus-visible:outline-offset-2",
  TOUCH_TARGET,
);

const V2_TITLE_CLASS = "font-auth-sans text-auth-text text-[22px] leading-[1.15] font-semibold tracking-[-0.03em] sm:text-[26px]";
const V2_DESCRIPTION_CLASS = cn("text-sm leading-relaxed", V2_MUTE);
const V2_EYEBROW_CLASS = "dark:border-auth-glass-line-inverted self-start px-3 py-1.5 dark:bg-white/5";
/** A quiet inset panel (stale agent, intro offer, comparison), the same surface as `V2Tiles`' card tiles. */
const V2_PANEL_CLASS =
  "border-auth-line bg-auth-field rounded-auth-control dark:border-auth-glass-line-inverted border p-3.5 text-sm dark:bg-white/5";
/** Two side by side where they fit, wrapping to one per row at 390px; never below the 44px floor. */
const V2_ACTION_FIT = "min-h-11 w-auto flex-1 basis-36";

function V2DialogContent({ children, maxWidth }: { children: ReactNode; maxWidth: string }) {
  return (
    <DialogPortal>
      <DialogOverlay className={V2_OVERLAY_CLASS} />
      <DialogPrimitive.Content
        className={cn(V2_CARD_CLASS, maxWidth)}
        aria-describedby="upgrade-prompt-description"
        data-theme-v2=""
      >
        {children}
        <DialogClose className={V2_CLOSE_CLASS}>
          <XIcon className="size-4" />
          <span className="sr-only">Close</span>
        </DialogClose>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

type LegacyVariant = "default" | "ghost" | "outline";

/**
 * One action button in either skin. Legacy keeps the exact `Button` it had;
 * V2 maps the legacy weight onto the two V2 buttons — `default` (the action
 * the prompt leads with) is the gradient CTA, everything else is outline.
 */
function PromptAction({
  v2,
  variant,
  className,
  onClick,
  children,
}: {
  v2: boolean;
  variant: LegacyVariant;
  className: string;
  onClick: () => void;
  children: ReactNode;
}) {
  if (v2) {
    return (
      <button
        type="button"
        className={cn(variant === "default" ? V2_PRIMARY_CTA : V2_OUTLINE_BUTTON, V2_ACTION_FIT)}
        onClick={onClick}
      >
        {children}
      </button>
    );
  }
  return (
    <Button variant={variant} className={className} onClick={onClick}>
      {children}
    </Button>
  );
}

/** Which V2 eyebrow a limit gets. An exclusion (limit 0) outranks the window. */
export function limitEyebrowKey(
  kind: LimitKind | null,
  limitKey: string | null,
  limit: number | undefined,
): "eyebrowDaily" | "eyebrowHourly" | "eyebrowExcluded" | "eyebrowPlan" {
  if (limit === 0) return "eyebrowExcluded";
  if (kind === "rate" && limitKey?.endsWith("_per_day")) return "eyebrowDaily";
  if (kind === "rate" && limitKey?.endsWith("_per_hour")) return "eyebrowHourly";
  return "eyebrowPlan";
}

/**
 * The bar fill for the current plan beside the target plan. Purely visual:
 * the numbers either side are the real limits. An unlimited target has no
 * finite ratio, so the current plan keeps a thin sliver rather than vanishing.
 */
function comparisonFill(current: number, target: number): number {
  if (target === -1) return 8;
  if (target <= 0) return 100;
  return Math.max(current > 0 ? 4 : 0, Math.min(100, Math.round((current / target) * 100)));
}

function LimitComparison({
  currentName,
  currentLimit,
  targetName,
  targetLimit,
  limitKey,
}: {
  currentName: string;
  currentLimit: number;
  targetName: string;
  targetLimit: number;
  limitKey: string | null;
}) {
  const t = useTranslations("V2Theme.limit");
  const locale = useLocale();
  const label = (value: number) => {
    if (value === -1) return t("unlimited");
    const count = new Intl.NumberFormat(locale).format(value);
    if (limitKey?.endsWith("_per_day")) return t("perDay", { count });
    if (limitKey?.endsWith("_per_hour")) return t("perHour", { count });
    return count;
  };
  return (
    <div
      role="group"
      aria-label={t("comparisonLabel", { plan: targetName })}
      className={cn(V2_PANEL_CLASS, "grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2.5 text-[13px]")}
      data-testid="limit-prompt-comparison"
    >
      <span className={cn("max-w-28 truncate", V2_MUTE)}>{currentName}</span>
      <span aria-hidden="true" className="bg-auth-line-soft dark:bg-white/10 block h-2 rounded-full">
        <span
          className="bg-auth-text block h-2 rounded-full"
          style={{ width: `${comparisonFill(currentLimit, targetLimit)}%` }}
          data-testid="limit-prompt-comparison-current"
        />
      </span>
      <span className={cn("text-end", V2_MUTE)}>{label(currentLimit)}</span>
      <span className="text-auth-text max-w-28 truncate font-semibold">{targetName}</span>
      <span aria-hidden="true" className="bg-auth-field-tint dark:bg-white/10 block h-2 rounded-full">
        <span className="block h-2 w-full rounded-full bg-[linear-gradient(90deg,var(--auth-iris),var(--auth-glow-cyan))]" />
      </span>
      <span className="text-auth-text text-end font-semibold">{label(targetLimit)}</span>
    </div>
  );
}

// ── ONB-023 contract ───────────────────────────────────────────────────────

/** What the user actually did with the prompt. `abandoned` = it was open and went away with it unresolved. */
export type LimitPromptAction =
  | "upgrade"
  | "manage_existing"
  | "wait"
  | "dismissed"
  | "abandoned";

/**
 * Everything an analytics emitter needs about one limit encounter, handed to
 * `onOutcome` exactly once per prompt. This component deliberately emits
 * nothing itself — ONB-023 owns the event catalog and the emission.
 *
 * `promptShown` is `true` here by construction (this callback only fires from
 * a prompt that rendered). The `false` case belongs to the publisher: see
 * `publishLimitReached`'s return value in `lib/limit-prompt-bus.ts`.
 */
export interface LimitPromptOutcome {
  /** What ran out, as the server named it: `"customAgent"`, `"chat.messages_per_day"`, … */
  resource: string | null;
  /** `"resource"` (a count that does not reset) or `"rate"` (a window that refills). */
  kind: LimitKind | null;
  /** The `PlanLimit` key evaluated, e.g. `"agent.max_count"`. */
  limitKey: string | null;
  /** The plan ceiling that was hit. `null` when the server did not send one. */
  limit: number | null;
  /** Usage against that ceiling. `null` when the server did not send one. */
  current: number | null;
  /** Always `true` from this callback — the prompt rendered. */
  promptShown: boolean;
  /** Which non-purchase route out the prompt actually offered, if any. */
  alternativeOffered: "delete_unused_agent" | "manage_existing" | "wait" | null;
  /** Which plan the upgrade CTA pointed at, when one was resolvable from the real catalog. */
  targetPlanSlug: string | null;
  /**
   * ONB-052 — whether the target plan's intro offer was actually ON SCREEN.
   *
   * The arm marker, and nothing more: there is no assignment here, no
   * bucketing and no cohort. It is `false` whenever the plan has no active
   * coupon (every plan today) or the coupon's terms were not renderable, and
   * it is the honest denominator for judging the offer against a holdout.
   * See `lib/limit-reached-analytics.ts` for why it cannot yet be emitted on
   * `limit_reached` itself.
   */
  introOfferShown: boolean;
  action: LimitPromptAction;
}

// ── Plan catalog ───────────────────────────────────────────────────────────

// ONB-034 — this file used to declare its OWN shape for `GET /plans`, a third
// description of the same response alongside `lib/plans.ts` and the endpoint's
// own DTO. It now imports the shared one, so a field added to the catalog
// reaches every reader or none.

interface UpgradeTarget {
  slug: string;
  name: string;
  /** The target plan's real ceiling for this limit key. `-1` = unlimited. */
  limit: number;
  /**
   * ONB-034 — whether this plan has a self-serve checkout at all.
   *
   * Decided here, from the catalog entry this function already holds, rather
   * than by asking a hardcoded `PLAN_CONFIG` map whether it happens to know
   * the slug. That map was one of the four competing sources of price truth
   * (finding F7) and is deleted; a plan added in the admin editor would never
   * have appeared in it, so the prompt would have sent the user to `/upgrade`
   * instead of to a checkout that exists.
   */
  purchasable: boolean;
  /**
   * ONB-051 — the plan's intro offer, already reduced to numbers this dialog
   * can render, or `null`. `null` is the normal case and makes every offer
   * element below disappear entirely.
   */
  introOffer: RenderableIntroOffer | null;
}

// ── ONB-051: the discounted entry ──────────────────────────────────────────
//
// Offered HERE, at the limit, and nowhere else — to someone who has just been
// stopped from doing something they wanted, where a priced on-ramp answers a
// question they are already asking. A discount at signup is handed to people
// who would have paid anyway.
//
// Three things this is not, each of which would be a different feature:
//   - It is NOT A TRIAL. Money is collected on day one. There is no
//     entitlement clock, no "days remaining" and deliberately no countdown
//     component anywhere in this block — the only clock in this file is the
//     rate-limit refill timer, which is about when a window refills, not
//     about when an offer dies.
//   - It is NOT INSTEAD OF FULL PRICE. Both buttons render, always, together.
//     Funnelling everyone through a discount defers revenue from the people
//     most ready to pay, and hiding the standard price to do it is the kind
//     of thing customers notice.
//   - It is NOT A DECISION THIS CODE MAKES. Whether an offer runs at all, at
//     what discount and for how long, is set on `PlanCoupon` in the admin and
//     resolved server-side into `introOffer` by `GET /plans`. Not one number
//     below is typed by hand, and switching the coupon off in the admin
//     removes every trace of this from the dialog with no deploy.

/** `introOffer` reduced to the two amounts and the one duration a dialog can say. */
export interface RenderableIntroOffer {
  /** The plan's standard monthly rate, in cents, straight from the catalog. */
  fullMonthlyCents: number;
  /** What each month of the intro period costs, in cents, after the coupon. */
  introMonthlyCents: number;
  /** How many months the intro rate lasts. `null` = for as long as the subscription does. */
  months: number | null;
  currency: string | null;
}

/**
 * Turns a catalog entry's `introOffer` into something renderable, or returns
 * `null`.
 *
 * `null` — the inert case — is returned for every one of these, on purpose:
 *   - no coupon on the plan, or a coupon switched off in the admin (the
 *     endpoint already resolves both to `introOffer: null`). This is the
 *     state of every plan today;
 *   - a plan with no price to discount;
 *   - a coupon carrying neither a percentage nor an amount;
 *   - a `duration` outside Stripe's own three-word vocabulary, or a
 *     `repeating` coupon with no month count. An offer whose length cannot be
 *     stated is one the dialog would have to be vague about, and vague is how
 *     a discount starts sounding like a trial;
 *   - an offer that does not actually cost less.
 *
 * Rounding is `Math.round`, matching Stripe's own percent-off arithmetic on
 * an integer-cent amount. This is a quote, not a charge — the charge is
 * Stripe's, from the same coupon.
 */
export function resolveIntroOffer(plan: PlanCatalogEntry): RenderableIntroOffer | null {
  const offer = plan.introOffer;
  if (!offer) return null;

  const fullMonthlyCents = plan.price;
  if (!Number.isFinite(fullMonthlyCents) || fullMonthlyCents <= 0) return null;

  let months: number | null;
  if (offer.duration === "once") months = 1;
  else if (offer.duration === "forever") months = null;
  else if (offer.duration === "repeating") {
    if (!Number.isInteger(offer.durationInMonths) || (offer.durationInMonths ?? 0) < 1) return null;
    months = offer.durationInMonths as number;
  } else return null;

  let introMonthlyCents: number;
  if (typeof offer.percentOff === "number" && offer.percentOff > 0 && offer.percentOff <= 100) {
    introMonthlyCents = Math.round(fullMonthlyCents * (1 - offer.percentOff / 100));
  } else if (typeof offer.amountOffCents === "number" && offer.amountOffCents > 0) {
    introMonthlyCents = Math.max(0, fullMonthlyCents - offer.amountOffCents);
  } else return null;

  // Two refusals, not one. An "offer" that costs the same or more is not an
  // offer. And an intro period that costs NOTHING is a free trial wearing a
  // coupon's clothes — money must be collected on day one or this dialog is
  // the wrong place for it, whatever the admin configured.
  if (introMonthlyCents <= 0 || introMonthlyCents >= fullMonthlyCents) return null;

  return { fullMonthlyCents, introMonthlyCents, months, currency: plan.currency ?? null };
}

/**
 * The checkout URL for one arm of the offer.
 *
 * Two things are deliberate here. `billing=monthly` is pinned whenever an
 * offer was on screen, because every amount this dialog quotes is a MONTHLY
 * rate and `/upgrade/[slug]` otherwise opens on annual — landing someone on a
 * different number than the one they just agreed to is the same defect as
 * quoting a hardcoded price. And the `none` arm returns the bare path,
 * unchanged from before this ticket, so a plan with no offer produces exactly
 * the URL it always did.
 */
export function upgradeHrefForArm(slug: string, arm: IntroOfferArm): string {
  if (arm === "none") return `/upgrade/${slug}`;
  const params = new URLSearchParams({ billing: "monthly", [INTRO_OFFER_QUERY_KEY]: arm });
  return `/upgrade/${slug}?${params.toString()}`;
}

/**
 * The first plan above the user's current one whose *real, seeded* limit for
 * this key is genuinely higher. Returns `null` when nothing higher exists, or
 * when we do not yet have the catalog or the current plan — in which case the
 * prompt shows no target number at all.
 *
 * This is the difference between "Pro gives you 25 agents" being a fact and
 * being a sentence someone typed in 2024. `lib/plans.ts` has marketing bullet
 * strings for the same numbers; they are not read here on purpose, because
 * nothing keeps them in step with `prisma/seeds/plans.ts`.
 */
export function resolveUpgradeTarget(
  catalog: PlanCatalogEntry[] | undefined,
  currentPlanSlug: string | undefined,
  limitKey: string | undefined,
  currentLimit: number | undefined,
): UpgradeTarget | null {
  if (!catalog || catalog.length === 0 || !limitKey || !currentPlanSlug) return null;

  const currentIndex = catalog.findIndex((p) => p.slug === currentPlanSlug);
  if (currentIndex < 0) return null;

  for (const candidate of catalog.slice(currentIndex + 1)) {
    const value = candidate.limits[limitKey];
    if (typeof value !== "number") continue;
    const purchasable = isPurchasablePlan(candidate);
    // Only a plan you can actually buy can carry an offer worth showing.
    const introOffer = purchasable ? resolveIntroOffer(candidate) : null;
    const base = { slug: candidate.slug, name: candidate.name, purchasable, introOffer };
    if (value === -1) return { ...base, limit: -1 };
    if (currentLimit !== undefined && value > currentLimit) return { ...base, limit: value };
    if (currentLimit === undefined && value > 0) return { ...base, limit: value };
  }
  return null;
}

// ── Copy for the things that can run out ───────────────────────────────────

interface ResourceCopy {
  /** Plural noun, used as "3 of 5 custom agents". */
  noun: string;
  /** Where the user manages the ones they already have. */
  manageHref?: string;
  /**
   * `UpgradePrompt` key for that link's label. A key rather than a string
   * because this table is module scope and `t` only exists inside the
   * component — the label still has to be translated.
   */
  manageLabelKey?: string;
}

/**
 * Keyed by whatever the server put in `resource` — the Prisma model for a
 * resource limit, the plan limit key for a rate limit. An unknown key is not
 * a failure: the prompt drops the noun and says less, rather than inventing a
 * label for something it does not recognize.
 */
const RESOURCE_COPY: Record<string, ResourceCopy> = {
  customAgent: {
    noun: "custom agents",
    manageHref: "/agents",
    manageLabelKey: "actions.reviewAgents",
  },
  knowledgeBase: {
    noun: "knowledge bases",
    manageHref: "/knowledge",
    manageLabelKey: "actions.reviewKnowledgeBases",
  },
  // Akili Flows — `FlowsService.assertFlowCountWithinPlan` names the resource
  // "flow". No manage link yet: a label key here must also be exercised by
  // upgrade-prompt.spec's catalogue sweep, which is outside the S3 home area.
  flow: { noun: "flows" },
  "chat.messages_per_hour": { noun: "messages" },
  "chat.messages_per_day": { noun: "messages" },
  "memory.imports_per_day": { noun: "memory imports" },
  "voice.stt_minutes_per_day": { noun: "minutes of voice input" },
  "voice.tts_minutes_per_day": { noun: "minutes of voice playback" },
};

/** "this hour" / "today" / "this month", derived from the key's own window suffix. */
function describeWindow(limitKey: string | undefined): string | null {
  if (!limitKey) return null;
  if (limitKey.endsWith("_per_hour")) return "this hour";
  if (limitKey.endsWith("_per_day")) return "today";
  if (limitKey.endsWith("_per_month")) return "this month";
  return null;
}

function formatTimeOfDay(date: Date): string {
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/** Deliberately vague words around an exact timestamp — "about 3 minutes", never "3 minutes". */
function formatCountdown(msRemaining: number): string {
  const minutes = Math.round(msRemaining / 60_000);
  if (minutes < 1) return "less than a minute";
  if (minutes === 1) return "about a minute";
  if (minutes < 60) return `about ${minutes} minutes`;
  const hours = Math.round(minutes / 60);
  if (hours === 1) return "about an hour";
  if (hours < 24) return `about ${hours} hours`;
  const days = Math.round(hours / 24);
  return days === 1 ? "about a day" : `about ${days} days`;
}

function formatDay(date: Date): string {
  return date.toLocaleDateString([], { month: "short", day: "numeric" });
}

/**
 * How long an agent must have sat untouched before the prompt will point at
 * it. Long enough that "you probably don't need this one" is a fair thing to
 * say to someone who is otherwise about to be sold a subscription.
 */
const STALE_AGENT_DAYS = 30;

/**
 * The stalest agent older than the cutoff, or `null`.
 *
 * HONEST LIMITATION, stated because the copy depends on it: `CustomAgent` has
 * `createdAt`/`updatedAt` and no `lastUsedAt` (see prisma/schema.prisma), so
 * this is "not edited in a month", not "not used in a month". The prompt says
 * exactly that and no more. Claiming an agent is unused on the strength of an
 * edit timestamp would be the same species of lie as showing a guessed limit.
 */
export function findStaleAgent(
  agents: CustomAgentDto[] | undefined,
  nowMs: number,
): { agent: CustomAgentDto; updatedAt: Date } | null {
  if (!agents || agents.length === 0) return null;
  const cutoff = nowMs - STALE_AGENT_DAYS * 86_400_000;

  let stalest: { agent: CustomAgentDto; updatedAt: Date } | null = null;
  for (const agent of agents) {
    const ts = Date.parse(agent.updatedAt);
    if (!Number.isFinite(ts) || ts >= cutoff) continue;
    if (!stalest || ts < stalest.updatedAt.getTime()) {
      stalest = { agent, updatedAt: new Date(ts) };
    }
  }
  return stalest;
}

/**
 * ONB-051 shipped a `withFallback(t, key, english)` helper beside every
 * `UpgradePrompt.introOffer.*` call below, because `messages/*.json` did not
 * yet carry those keys and next-intl renders a missing message as its own key
 * path. ONB-064 merged and translated them into all ten locale files, so the
 * helper had nothing left to fall back FROM and was removed with its
 * `Translator` type: a missing key is now a real defect to fix in the catalog
 * rather than something papered over silently at the call site.
 *
 * NOTE: the `RESOURCE_COPY` nouns above still hold English literals.
 * Converting those is a separate change and deliberately not smuggled in here.
 */

// ── Component ──────────────────────────────────────────────────────────────

interface UpgradePromptProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  feature?: string;
  title?: string;
  description?: string;
  requiredPlan?: string;
  /**
   * ONB-021 — the intercepted limit rejection (`lib/api-client.ts`). When
   * present the prompt renders the limit variant, built from this error's own
   * numbers plus the plan catalog. When absent it renders the original
   * feature-gate variant, unchanged.
   */
  limitError?: LimitReachedError;
  /** Fired exactly once per limit prompt. See {@link LimitPromptOutcome}. */
  onOutcome?: (outcome: LimitPromptOutcome) => void;
}

/**
 * Two dialogs behind one name, split on purpose rather than branched inside
 * one component.
 *
 * The limit variant reads the plan, the plan catalog and (for agents) the
 * agent list. `FeatureGate` mounts this component permanently with
 * `open={false}`, and `app/(site)/providers.tsx` mounts the host on every route
 * including `/auth` — so a single component doing that fetching in its body
 * would fire `GET /user/plan` for every visitor on every page, signed in or
 * not. On `/auth` that is a guaranteed 401, which this client treats as a
 * real session expiry after its retry and answers by destroying the session.
 * Keeping the fetching in a component that only mounts when there is a limit
 * to explain is what makes that impossible rather than merely unlikely.
 */
export function UpgradePrompt(props: UpgradePromptProps) {
  const { limitError, ...rest } = props;
  if (limitError) {
    return <LimitPrompt {...rest} limitError={limitError} />;
  }
  return <FeatureGatePrompt {...rest} />;
}

// ── The original feature-gate variant, unchanged in behavior ───────────────

function FeatureGatePrompt({
  open,
  onOpenChange,
  feature,
  title = "Upgrade your plan",
  description,
  requiredPlan,
}: Omit<UpgradePromptProps, "limitError">) {
  const router = useRouter();
  const t = useTranslations("UpgradePrompt");
  const tV2 = useTranslations("V2Theme.limit");
  const v2 = useAuthThemeV2();

  const resolvedDescription =
    description ??
    (feature
      ? `The "${feature}" feature requires a higher-tier plan.`
      : "Unlock more features by upgrading your plan.");

  const planBadgeLabel = requiredPlan
    ? requiredPlan.charAt(0).toUpperCase() + requiredPlan.slice(1)
    : "Pro";

  const actionClass = cn(EDITOR_CONTROL_CLASS, "px-4");

  const body = (
    <>
      <DialogHeader className={v2 ? "gap-3 text-left" : undefined}>
        {v2 ? (
          <MonoEyebrow variant="pill" className={V2_EYEBROW_CLASS}>
            {tV2("eyebrowExcluded")}
          </MonoEyebrow>
        ) : null}
        <DialogTitle className={v2 ? V2_TITLE_CLASS : undefined}>{title}</DialogTitle>
        <DialogDescription
          id="upgrade-prompt-description"
          className={v2 ? V2_DESCRIPTION_CLASS : undefined}
        >
          {resolvedDescription}
        </DialogDescription>
      </DialogHeader>

      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold">Required plan:</span>
          <span
            className={cn(
              "brand-gradient rounded-full px-2.5 py-0.5 text-xs font-semibold text-white",
              v2 && "font-auth-sans",
            )}
          >
            {planBadgeLabel}
          </span>
        </div>

        <div className="flex flex-wrap justify-end gap-2" data-testid="limit-prompt-actions">
          <PromptAction
            v2={v2}
            variant="ghost"
            className={actionClass}
            onClick={() => onOpenChange(false)}
          >
            {t("actions.notNow")}
          </PromptAction>
          <PromptAction
            v2={v2}
            variant="default"
            className={actionClass}
            onClick={() => {
              onOpenChange(false);
              router.push("/?settings=billing");
            }}
          >
            {t("actions.viewPlans")}
          </PromptAction>
        </div>
      </div>
    </>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {v2 ? (
        <V2DialogContent maxWidth="max-w-sm">{body}</V2DialogContent>
      ) : (
        <DialogContent
          className={cn(DIALOG_VIEWPORT_CLASS, "max-w-sm")}
          aria-describedby="upgrade-prompt-description"
        >
          {body}
        </DialogContent>
      )}
    </Dialog>
  );
}

// ── ONB-021: the limit variant ─────────────────────────────────────────────

function LimitPrompt({
  open,
  onOpenChange,
  limitError,
  onOutcome,
}: Omit<UpgradePromptProps, "limitError"> & { limitError: LimitReachedError }) {
  // ONB-064 — money must be formatted for the reader, not pinned to
  // en-US. Server and client both resolve this to the same value for
  // the same render, so it is hydration-safe.
  const locale = useLocale();
  const router = useRouter();
  const t = useTranslations("UpgradePrompt");
  const tV2 = useTranslations("V2Theme.limit");
  const v2 = useAuthThemeV2();
  const { plan } = usePlan();
  const { data: activeOrg } = useActiveOrganization();

  const kind = limitError.kind;
  const resource = limitError.resource ?? null;
  const limitKey = limitError.limitKey ?? null;
  const copy = resource ? RESOURCE_COPY[resource] : undefined;

  // The ceiling: the server's own number first, and only if it did not send
  // one, the same user's plan limits from usePlan() — the identical
  // `PlanLimit` row read through a different endpoint, not a guess. If
  // neither has it, no number is shown at all.
  const planLimit = limitKey ? plan?.limits?.[limitKey] : undefined;
  const limit =
    limitError.limit ?? (typeof planLimit === "number" && planLimit >= 0 ? planLimit : undefined);
  const current = limitError.current;

  // A live clock, so a prompt left open does not keep claiming the window
  // refills "about 2 minutes" from a moment that has since gone by.
  const [nowMs, setNowMs] = useState(() => Date.now());
  const hasCountdown = open && limitError.resetsAt !== undefined;
  useEffect(() => {
    if (!hasCountdown) return;
    const id = setInterval(() => setNowMs(Date.now()), 15_000);
    return () => clearInterval(id);
  }, [hasCountdown]);

  const { data: catalog } = useQuery({
    queryKey: ["plan-catalog"],
    queryFn: () => api.get<PlanCatalogEntry[]>("/plans"),
    // Public price-list data, identical for every user — but only worth
    // fetching while a prompt is actually on screen asking about it.
    enabled: open,
    staleTime: 10 * 60_000,
    retry: false,
  });

  // Shares `useAgents()`'s exact query key, so this reuses an already-cached
  // agent list on the surface most likely to hit this limit instead of
  // refetching, and never fetches at all for any other resource.
  const { data: agents } = useQuery({
    queryKey: ["agents", activeOrg?.id ?? null],
    queryFn: () => api.get<CustomAgentDto[]>("/agents"),
    enabled: open && resource === "customAgent",
    staleTime: 60_000,
    retry: false,
  });

  const staleAgent = useMemo(() => findStaleAgent(agents, nowMs), [agents, nowMs]);

  const target = useMemo(
    () => resolveUpgradeTarget(catalog, plan?.planSlug, limitKey ?? undefined, limit),
    [catalog, plan?.planSlug, limitKey, limit],
  );

  // ONB-051 — the single value every offer element below hangs off. `null`
  // for a plan with no active coupon, which is every plan today: in that case
  // nothing here renders, no label changes and no URL grows a parameter, so
  // the dialog is exactly the dialog that shipped before this ticket.
  const introOffer = target?.introOffer ?? null;

  // A rate limit that refills is the one case where waiting is a real answer.
  // `resetsAt` is only ever set when it is genuinely in the future (see
  // api-client's parseResetsAt), and a plan limit of 0 means the plan does
  // not include this at all — no amount of waiting produces a message.
  const resetsAt = kind === "rate" ? limitError.resetsAt : undefined;
  const canWait = resetsAt !== undefined && limit !== 0;

  const alternativeOffered: LimitPromptOutcome["alternativeOffered"] = canWait
    ? "wait"
    : staleAgent
      ? "delete_unused_agent"
      : copy?.manageHref
        ? "manage_existing"
        : null;

  // ── Outcome, fired exactly once ─────────────────────────────────────────
  const firedRef = useRef(false);
  const latestRef = useRef({ onOutcome, limitKey, limit, current, kind, resource, alternativeOffered, target, introOffer });
  latestRef.current = { onOutcome, limitKey, limit, current, kind, resource, alternativeOffered, target, introOffer };

  useEffect(() => {
    if (open) firedRef.current = false;
  }, [open, limitError]);

  const fireOutcome = useCallback((action: LimitPromptAction) => {
    const l = latestRef.current;
    if (firedRef.current || !l.onOutcome) return;
    firedRef.current = true;
    l.onOutcome({
      resource: l.resource,
      kind: l.kind,
      limitKey: l.limitKey,
      limit: l.limit ?? null,
      current: l.current ?? null,
      promptShown: true,
      alternativeOffered: l.alternativeOffered,
      targetPlanSlug: action === "upgrade" ? (l.target?.slug ?? null) : null,
      // Reported for EVERY action, not just `upgrade`: a dismissal with the
      // offer on screen is the observation that pays for the holdout.
      introOfferShown: l.introOffer !== null,
      action,
    });
  }, []);

  // Navigating away, or unmounting the host, with the prompt still open is a
  // real outcome — not the absence of one. Without this, every abandoned
  // prompt would silently look like it never happened. A dismissal has
  // already set the guard by the time this runs, so it never double-counts.
  const openRef = useRef(open);
  openRef.current = open;
  useEffect(
    () => () => {
      if (openRef.current) fireOutcome("abandoned");
    },
    [fireOutcome],
  );

  const close = (action: LimitPromptAction) => {
    fireOutcome(action);
    onOpenChange(false);
  };

  const handleOpenChange = (next: boolean) => {
    // Escape, the overlay click and the X all land here. Keyboard-dismissible
    // with no extra wiring, and Radix returns focus to whatever held it
    // before the prompt interrupted — the focus trap lasts exactly as long as
    // the dialog does.
    if (!next) fireOutcome("dismissed");
    onOpenChange(next);
  };

  const handleUpgrade = (arm: IntroOfferArm) => {
    close("upgrade");
    // `/upgrade/[slug]` is a checkout page; a free or contact-sales plan has
    // none. Sending someone to a route that throws would be a worse outcome
    // than the limit they started with.
    router.push(target?.purchasable === true ? upgradeHrefForArm(target.slug, arm) : "/upgrade");
  };

  const handleManage = () => {
    close("manage_existing");
    if (copy?.manageHref) router.push(copy.manageHref);
  };

  // ── Copy assembly ───────────────────────────────────────────────────────

  const windowPhrase = kind === "rate" ? describeWindow(limitKey ?? undefined) : null;
  const noun = copy?.noun;

  const resolvedTitle = (() => {
    if (limit === 0) {
      return noun ? `Your plan doesn't include ${noun}` : "Your plan doesn't include this";
    }
    if (kind === "rate") {
      return noun
        ? `You've used all your ${noun}${windowPhrase ? ` ${windowPhrase}` : ""}`
        : "You've hit a rate limit";
    }
    if (limit !== undefined && noun) return `You've used all ${limit} ${noun}`;
    if (noun) return `You've reached your ${noun} limit`;
    return "You've reached a plan limit";
  })();

  // V2 — the one variant whose headline carries a gradient accent, on its
  // "{limit} {noun}" tail. The whole sentence stays one text node for
  // assistive tech and text queries (`sr-only`); the split, accented copy is
  // the visual twin and is hidden from the accessibility tree.
  const accentTail =
    limit !== undefined && limit !== 0 && kind !== "rate" && noun ? `${limit} ${noun}` : null;
  const titleNode =
    v2 && accentTail && resolvedTitle.endsWith(accentTail) ? (
      <>
        <span className="sr-only">{resolvedTitle}</span>
        <span aria-hidden="true">
          {resolvedTitle.slice(0, resolvedTitle.length - accentTail.length)}
          <V2Accent>{accentTail}</V2Accent>
        </span>
      </>
    ) : (
      resolvedTitle
    );

  const usageLine = (() => {
    if (limit === undefined || limit === 0) return null;
    // A rate-limit counter is incremented before the check, so `current` can
    // legitimately come back one past the ceiling. "26 of 25" is confusing
    // noise, so the count is only rendered when it reads truthfully.
    if (current === undefined || current > limit) {
      return noun ? `Your plan allows ${limit} ${noun}${windowPhrase ? ` ${windowPhrase}` : ""}.` : null;
    }
    return noun
      ? `${current} of ${limit} ${noun}${windowPhrase ? ` ${windowPhrase}` : ""}.`
      : `${current} of ${limit} used.`;
  })();

  const waitLine =
    canWait && resetsAt
      ? `This refills at ${formatTimeOfDay(resetsAt)} — ${formatCountdown(resetsAt.getTime() - nowMs)} from now. Waiting costs nothing.`
      : null;

  // A plan limit of 0 is not a shortage, it is an exclusion — no amount of
  // waiting turns it into one message, and saying otherwise would be the
  // rate-limit variant lying.
  const excludedLine =
    limit === 0
      ? `${plan?.planName ?? "Your current plan"} allows none of these, so waiting won't help.`
      : null;

  const targetLine = (() => {
    if (!target) return null;
    if (target.limit === -1) {
      return noun ? `${target.name} makes ${noun} unlimited.` : `${target.name} removes this limit.`;
    }
    return noun
      ? `${target.name} allows ${target.limit} ${noun}${windowPhrase ? ` ${windowPhrase}` : ""}.`
      : `${target.name} raises this to ${target.limit}.`;
  })();

  // ── ONB-051 copy ────────────────────────────────────────────────────────
  //
  // Every amount below comes from `formatMoneyCents` over a cents value the
  // catalog sent. No percentage, price or month count is written here.
  const introPrice = introOffer
    ? formatMoneyCents(introOffer.introMonthlyCents, introOffer.currency, locale)
    : "";
  const fullPrice = introOffer
    ? formatMoneyCents(introOffer.fullMonthlyCents, introOffer.currency, locale)
    : "";

  const offerLine = (() => {
    if (!introOffer || !target) return null;
    const vars = {
      plan: target.name,
      introPrice,
      fullPrice,
      months: introOffer.months ?? 0,
    };
    if (introOffer.months === null) {
      return t("introOffer.forever", vars);
    }
    if (introOffer.months === 1) {
      return t("introOffer.firstMonth", vars);
    }
    return t("introOffer.firstMonths", vars);
  })();

  // Said plainly and unconditionally alongside the offer. This is the line
  // that keeps a discount from being read as a trial: there is no free
  // period, no expiry and nothing to remember to cancel before.
  const offerTermsLine = introOffer ? t("introOffer.notATrial", { introPrice }) : null;

  const resolvedDescription =
    [usageLine, excludedLine, waitLine].filter(Boolean).join(" ") ||
    "You've reached a limit on your current plan.";

  const actionClass = cn(EDITOR_CONTROL_CLASS, "px-4");

  // Both purchase paths, always rendered as a pair. The full price keeps the
  // weight it already had (primary in the resource variant, a quiet ghost in
  // the rate variant); the discounted entry sits beside it one step quieter.
  // Deliberately not the other way round — the discount is worth offering, it
  // is not worth steering someone into who was ready to pay the real price.
  const fullPriceLabel = introOffer ? t("introOffer.fullPriceCta", { fullPrice }) : null;
  const introPriceLabel = introOffer ? t("introOffer.cta", { introPrice }) : null;

  // V2 — the comparison only when every number on it is a real one: the
  // ceiling that was hit, the resolved target's ceiling and the plan name.
  const comparison =
    v2 && target && limit !== undefined && plan?.planName ? (
      <LimitComparison
        currentName={plan.planName}
        currentLimit={limit}
        targetName={target.name}
        targetLimit={target.limit}
        limitKey={limitKey}
      />
    ) : null;

  const panelClass = v2 ? V2_PANEL_CLASS : "rounded-md border border-border bg-muted/40 p-3 text-sm";

  const body = (
    <>
      <DialogHeader className={v2 ? "gap-3 text-left" : undefined}>
        {v2 ? (
          <MonoEyebrow variant="pill" className={V2_EYEBROW_CLASS}>
            {tV2(limitEyebrowKey(kind, limitKey, limit))}
          </MonoEyebrow>
        ) : null}
        <DialogTitle className={v2 ? V2_TITLE_CLASS : undefined}>{titleNode}</DialogTitle>
        {/* Radix points the dialog's accessible description at this node, so a
            screen reader reads the real numbers immediately on open — the prompt
            interrupts someone mid-task and has to say why without being hunted for. */}
        <DialogDescription
          id="upgrade-prompt-description"
          className={v2 ? V2_DESCRIPTION_CLASS : undefined}
        >
          {resolvedDescription}
        </DialogDescription>
      </DialogHeader>

      <div className="flex flex-col gap-4">
        {/* The alternative comes before the CTA, deliberately. Someone with an
            agent they have not touched in a month does not need to buy anything. */}
        {staleAgent ? (
          <p className={panelClass}>
            <span className="font-semibold">{staleAgent.agent.name}</span> hasn&apos;t been edited
            since {formatDay(staleAgent.updatedAt)}. Deleting it frees a slot straight away, at no
            cost.
          </p>
        ) : null}

        {comparison}

        {targetLine ? (
          <p className={v2 ? cn("text-sm", V2_MUTE) : "text-sm text-muted-foreground"}>{targetLine}</p>
        ) : null}

        {/* ONB-051 — absent entirely unless the catalog resolved a live
            coupon into renderable terms. No timer, no expiry, nothing that
            counts down: the two sentences state a price and a duration. */}
        {introOffer ? (
          <div className={panelClass} data-testid="limit-prompt-intro-offer">
            <p>{offerLine}</p>
            <p className={v2 ? cn("mt-1 text-xs", V2_MUTE) : "mt-1 text-xs text-muted-foreground"}>
              {offerTermsLine}
            </p>
          </div>
        ) : null}

        <div className="flex flex-wrap justify-end gap-2" data-testid="limit-prompt-actions">
          {canWait ? (
            <>
              {target && introOffer ? (
                <>
                  <PromptAction
                    v2={v2}
                    variant="ghost"
                    className={actionClass}
                    onClick={() => handleUpgrade("shown")}
                  >
                    {fullPriceLabel}
                  </PromptAction>
                  <PromptAction
                    v2={v2}
                    variant="ghost"
                    className={actionClass}
                    onClick={() => handleUpgrade("taken")}
                  >
                    {introPriceLabel}
                  </PromptAction>
                </>
              ) : target ? (
                <PromptAction v2={v2} variant="ghost" className={actionClass} onClick={() => handleUpgrade("none")}>
                  {t("actions.seePlan", { plan: target.name })}
                </PromptAction>
              ) : null}
              <PromptAction v2={v2} variant="default" className={actionClass} onClick={() => close("wait")}>
                {t("actions.gotItIllWait")}
              </PromptAction>
            </>
          ) : (
            <>
              <PromptAction v2={v2} variant="ghost" className={actionClass} onClick={() => close("dismissed")}>
                {t("actions.notNow")}
              </PromptAction>
              {copy?.manageHref && copy.manageLabelKey ? (
                <PromptAction
                  v2={v2}
                  variant={staleAgent ? "default" : "outline"}
                  className={actionClass}
                  onClick={handleManage}
                >
                  {t(copy.manageLabelKey)}
                </PromptAction>
              ) : null}
              {target && introOffer ? (
                <>
                  <PromptAction
                    v2={v2}
                    variant={staleAgent ? "outline" : "default"}
                    className={actionClass}
                    onClick={() => handleUpgrade("shown")}
                  >
                    {fullPriceLabel}
                  </PromptAction>
                  <PromptAction
                    v2={v2}
                    variant="outline"
                    className={actionClass}
                    onClick={() => handleUpgrade("taken")}
                  >
                    {introPriceLabel}
                  </PromptAction>
                </>
              ) : target ? (
                <PromptAction
                  v2={v2}
                  variant={staleAgent ? "outline" : "default"}
                  className={actionClass}
                  onClick={() => handleUpgrade("none")}
                >
                  {t("actions.upgradeToPlan", { plan: target.name })}
                </PromptAction>
              ) : null}
            </>
          )}
        </div>
      </div>
    </>
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {v2 ? (
        <V2DialogContent maxWidth="max-w-[30rem]">{body}</V2DialogContent>
      ) : (
        <DialogContent
          className={cn(DIALOG_VIEWPORT_CLASS, "max-w-sm")}
          aria-describedby="upgrade-prompt-description"
        >
          {body}
        </DialogContent>
      )}
    </Dialog>
  );
}
